import { formatUnits } from 'ethers'

import { Account } from '@ambire-common/interfaces/account'
import { Network } from '@ambire-common/interfaces/network'
import {
  SelectedAccountPortfolio,
  SelectedAccountPortfolioTokenResult
} from '@ambire-common/interfaces/selectedAccount'
import { GasTankTokenResult } from '@ambire-common/libs/portfolio'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import getAndFormatTokenDetails from '@common/modules/dashboard/helpers/getTokenDetails'

const parseGasTankToken = (token: GasTankTokenResult, amount: bigint) => {
  const { availableAmount, ...rest } = token

  return { ...rest, amount } as SelectedAccountPortfolioTokenResult
}

/**
 * The relayer reports two numbers per gas-tank entry: `amount`, which already
 * counts a top-up its indexer has seen but not yet settled, and
 * `availableAmount`, the part that can actually pay a fee right now. The fee
 * flow spends `availableAmount` (see `libs/estimate/ambireEstimation.ts`), so
 * any surface that headlines `amount` promises a balance the Send screen then
 * refuses with "Insufficient amount" - Trello #448.
 *
 * No `|| amount` fallback here: PortfolioController substitutes `amount` when
 * the relayer omits the field, so a `0n` that reaches this point means a
 * deposit is in flight and nothing is spendable yet. Treating that 0 as the
 * full deposit is what offered a Gas Tank fee the relayer refused outright
 * with "You have no Gas Tank balance in that token" - Trello #521.
 */
export const getSpendableGasTankAmount = (
  token: Pick<GasTankTokenResult, 'amount' | 'availableAmount'>
): bigint => token.availableAmount

/** The part of a gas-tank entry that is deposited but not yet spendable. */
export const getPendingGasTankAmount = (
  token: Pick<GasTankTokenResult, 'amount' | 'availableAmount'>
): bigint => {
  const pending = token.amount - getSpendableGasTankAmount(token)

  return pending > 0n ? pending : 0n
}

export type GasTankTokenDetails = {
  token: SelectedAccountPortfolioTokenResult
  symbol: string
  balanceFormatted: string | null
  balanceUSDFormatted: string | null
  /**
   * How many (chainId, token) entries were folded into this row, and the
   * largest of them. A fee is settled against ONE entry — the relayer does not
   * pool them — so a split balance can cover less per transaction than the
   * total suggests. Only worth surfacing when `entryCount > 1`.
   */
  entryCount: number
  maxSpendableFormatted: string | null
  /**
   * The deposited-but-not-yet-spendable part of this row, formatted, or `null`
   * when the whole balance has settled. See `getPendingGasTankAmount`.
   */
  pendingFormatted: string | null
}

const rescaleAmount = (amount: bigint, fromDecimals: number, toDecimals: number): bigint => {
  if (fromDecimals === toDecimals) return amount

  return fromDecimals < toDecimals
    ? amount * 10n ** BigInt(toDecimals - fromDecimals)
    : amount / 10n ** BigInt(fromDecimals - toDecimals)
}

/**
 * The tank is one cross-chain pot — a deposit made on any network is spendable
 * as fees on every supported network — but the relayer still reports it per
 * (chainId, token). Shown raw, a user who topped up USDC on three chains sees
 * three indistinguishable "USDC" rows and can't tell their total. Fold entries
 * of the same symbol into one, mirroring `collapseGasTankFeeOptions` in the
 * "Pay fee with" flow.
 */
export const aggregateGasTankTokensBySymbol = (
  gasTankTokens: GasTankTokenResult[]
): GasTankTokenResult[] => {
  const groups = new Map<string, GasTankTokenResult[]>()

  gasTankTokens.forEach((gasTankToken) => {
    const key = gasTankToken.symbol.toLowerCase()
    const group = groups.get(key)

    if (group) group.push(gasTankToken)
    else groups.set(key, [gasTankToken])
  })

  return Array.from(groups.values()).map((group) => {
    const representative = group.reduce((best, candidate) =>
      candidate.amount > best.amount ? candidate : best
    )
    if (group.length === 1) return representative

    const sumBy = (getAmount: (entry: GasTankTokenResult) => bigint) =>
      group.reduce(
        (sum, entry) =>
          sum + rescaleAmount(getAmount(entry), entry.decimals, representative.decimals),
        0n
      )

    return {
      ...representative,
      amount: sumBy((entry) => entry.amount),
      availableAmount: sumBy((entry) => entry.availableAmount),
      // The relayer prices some entries at $0 (e.g. WPLS on 369) — borrow a
      // priced sibling so the merged row doesn't lose its USD value.
      priceIn: representative.priceIn?.length
        ? representative.priceIn
        : (group.find((entry) => entry.priceIn?.length)?.priceIn ?? representative.priceIn)
    }
  })
}

/**
 * The relayer credits the gas tank per (chainId, token) — an account can hold
 * several at once (e.g. USDC on BNB Chain + WPLS on PulseChain). This used to
 * read `gasTankTokens[0]` only, so every deposit after the first one was
 * invisible in the UI even though the relayer had credited it. Return the whole
 * list (same-symbol entries folded into one row — see
 * `aggregateGasTankTokensBySymbol`), plus the USD total the portfolio already
 * sums across all of them.
 */
export const getGasTankTokenDetails = (
  portfolio: SelectedAccountPortfolio,
  account: Account | null,
  networks: Network[]
) => {
  const gasTankState = portfolio?.portfolioState?.gasTank
  const gasTankResult = gasTankState?.result as
    | { gasTankTokens: GasTankTokenResult[]; total?: { [baseCurrency: string]: number } }
    | undefined

  const noAccount = !account || !account.addr
  const noPortfolio = !portfolio || !portfolio.portfolioState || !gasTankState
  const noGasTankResult = !gasTankResult || !('gasTankTokens' in gasTankResult)
  const noGasTankTokens =
    noGasTankResult ||
    !Array.isArray(gasTankResult.gasTankTokens) ||
    gasTankResult.gasTankTokens.length === 0

  /**
   * "Nothing deposited yet" and "we couldn't load the tank" both end up with no
   * rows, but they are NOT the same thing to the UI: the relayer answers a
   * brand-new account with a perfectly successful `{gasTank: {balance: []}}`,
   * and treating that as a failure is what made a freshly created account read
   * "Gas Tank Unavailable" and blocked the top-up it needs to stop being empty
   * (Trello #478). A successful fetch sets `isReady` with a `gasTankTokens`
   * array; a failed one only sets `criticalError` and leaves `isReady` false.
   */
  const isUnavailable = noAccount || noPortfolio || !gasTankState?.isReady || noGasTankResult

  if (noAccount || noPortfolio || noGasTankResult || noGasTankTokens) {
    return {
      isUnavailable,
      token: null,
      tokens: [] as GasTankTokenDetails[],
      balanceFormatted: null,
      balanceUSDFormatted: null,
      pendingFormatted: null
    }
  }

  const entriesBySymbol = new Map<string, GasTankTokenResult[]>()
  gasTankResult.gasTankTokens.forEach((entry) => {
    const symbolKey = entry.symbol.toLowerCase()
    const group = entriesBySymbol.get(symbolKey)

    if (group) group.push(entry)
    else entriesBySymbol.set(symbolKey, [entry])
  })

  const tokens: GasTankTokenDetails[] = aggregateGasTankTokensBySymbol(
    gasTankResult.gasTankTokens
  ).map((gasTankToken) => {
    // Every number below is the SPENDABLE balance, never the raw `amount` — see
    // `getSpendableGasTankAmount`.
    const token = parseGasTankToken(gasTankToken, getSpendableGasTankAmount(gasTankToken))
    const details = getAndFormatTokenDetails(token, networks)
    const group = entriesBySymbol.get(gasTankToken.symbol.toLowerCase()) ?? []
    const largest = group.reduce<GasTankTokenResult | null>(
      (best, entry) =>
        !best ||
        rescaleAmount(getSpendableGasTankAmount(entry), entry.decimals, gasTankToken.decimals) >
          rescaleAmount(getSpendableGasTankAmount(best), best.decimals, gasTankToken.decimals)
          ? entry
          : best,
      null
    )
    const pending = getPendingGasTankAmount(gasTankToken)

    return {
      token,
      symbol: gasTankToken.symbol,
      balanceFormatted: details.balanceFormatted,
      balanceUSDFormatted: details.balanceUSDFormatted,
      entryCount: group.length,
      maxSpendableFormatted:
        group.length > 1 && largest
          ? getAndFormatTokenDetails(
              parseGasTankToken(largest, getSpendableGasTankAmount(largest)),
              networks
            ).balanceFormatted
          : null,
      pendingFormatted: pending
        ? getAndFormatTokenDetails(parseGasTankToken(gasTankToken, pending), networks)
            .balanceFormatted
        : null
    }
  })

  // NOT `gasTankResult.total` — the portfolio controller sums that over the raw
  // `amount`, so a pending top-up would inflate the headline past what the rows
  // (and the "Pay fee with" list) can actually spend. Re-sum the spendable USD
  // value of the rows we just built instead.
  const totalUSD = tokens.reduce<number | null>((sum, row) => {
    const price = row.token.priceIn?.find(({ baseCurrency }) => baseCurrency === 'usd')?.price

    if (!price) return sum

    return (sum ?? 0) + Number(formatUnits(row.token.amount, row.token.decimals)) * price
  }, null)

  const [first] = tokens as [GasTankTokenDetails, ...GasTankTokenDetails[]]

  return {
    isUnavailable: false,
    // `token` / `balanceFormatted` stay first-token for callers that render a
    // single line; `tokens` is the full list.
    token: first.token,
    tokens,
    balanceFormatted: first.balanceFormatted,
    // A single string for the surfaces that only have room for a one-line hint.
    pendingFormatted:
      tokens
        .filter((row) => !!row.pendingFormatted)
        .map((row) => `${row.pendingFormatted} ${row.symbol}`)
        .join(' · ') || null,
    balanceUSDFormatted:
      typeof totalUSD === 'number' ? formatDecimals(totalUSD, 'value') : first.balanceUSDFormatted
  }
}
